import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Calendar, MapPin, Search, Filter, Heart, List, Map as MapIcon, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import GoogleMapView from '../components/GoogleMapView';
import EventMediaGallery from '../components/EventMediaGallery';
import FavoriteButton from '../components/FavoriteButton';
import { PAYMENTS_ENABLED } from '../config/features';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Arte', 'Cultura', 'Deporte', 'Educación', 'Gastronomía', 'Música', 'Solidario', 'Tecnología', 'Turismo'];

const Explorar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [showSolidarity, setShowSolidarity] = useState(searchParams.get('solidarity') === 'true');
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'map' ? 'map' : 'list');

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, showSolidarity]);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (showSolidarity) params.append('is_solidarity', 'true');
      params.append('limit', '50');

      const response = await axios.get(`${API}/events?${params}`);
      setEvents(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.description_short.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCategoryChange = (category) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
  };

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8" data-testid="explore-header">
          <h1 className="text-4xl sm:text-5xl font-heading font-black text-[#0A0A0A] mb-4 tracking-tight">Explorar Eventos</h1>
          <p className="text-lg text-[#737373]">Descubrí eventos cerca tuyo</p>
        </div>

        {/* Search & Filters */}
        <div className="bg-white/50 backdrop-blur-sm border border-[#E5E5E5] rounded-lg p-6 mb-8 space-y-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#737373]" />
            <Input
              placeholder="Buscar eventos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-[#E5E5E5] text-[#0A0A0A] focus:border-primary"
              data-testid="search-input"
            />
          </div>

          {/* Categories */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-5 h-5 text-[#737373]" />
              <h3 className="text-sm font-semibold text-[#0A0A0A]">Categorías</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === cat
                      ? 'bg-primary text-white'
                      : 'bg-[#F5F5F5] text-[#525252] hover:bg-[#E5E5E5]'
                  }`}
                  data-testid={`filter-category-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Solidarity Filter */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="solidarity"
              checked={showSolidarity}
              onChange={(e) => setShowSolidarity(e.target.checked)}
              className="w-5 h-5 rounded border-[#E5E5E5] bg-white text-primary focus:ring-primary"
              data-testid="solidarity-filter"
            />
            <label htmlFor="solidarity" className="flex items-center gap-2 text-[#0A0A0A] cursor-pointer">
              <Heart className="w-5 h-5 text-primary" />
              Solo eventos solidarios
            </label>
          </div>

          {/* Clear Filters */}
          {(selectedCategory || showSolidarity) && (
            <Button
              onClick={() => { setSelectedCategory(''); setShowSolidarity(false); }}
              variant="outline"
              size="sm"
              className="border-[#E5E5E5] text-[#525252] hover:bg-[#F5F5F5]"
              data-testid="clear-filters-button"
            >
              Limpiar filtros
            </Button>
          )}
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
            <p className="text-sm text-[#737373]">
              {filteredEvents.length} evento{filteredEvents.length !== 1 ? 's' : ''} encontrado{filteredEvents.length !== 1 ? 's' : ''}
            </p>

            <div className="inline-flex rounded-lg border border-[#E5E5E5] bg-white p-1" data-testid="view-mode-toggle">
              <button
                onClick={() => { setViewMode('list'); const p = new URLSearchParams(searchParams); p.delete('view'); setSearchParams(p); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-primary text-white' : 'text-[#525252] hover:bg-[#F5F5F5]'
                }`}
                data-testid="view-mode-list"
              >
                <List className="w-4 h-4" />
                Lista
              </button>
              <button
                onClick={() => { setViewMode('map'); const p = new URLSearchParams(searchParams); p.set('view', 'map'); setSearchParams(p); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'map' ? 'bg-primary text-white' : 'text-[#525252] hover:bg-[#F5F5F5]'
                }`}
                data-testid="view-mode-map"
              >
                <MapIcon className="w-4 h-4" />
                Mapa
              </button>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="bg-white rounded-lg h-64"></div>
                </div>
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-20">
              <Calendar className="w-16 h-16 text-[#737373] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#525252] mb-2">No se encontraron eventos</h3>
              <p className="text-[#737373]">Intentá cambiar los filtros o la búsqueda</p>
            </div>
          ) : viewMode === 'map' ? (
            <div data-testid="explore-map-view">
              <GoogleMapView
                showEventInfo
                height="600px"
                markers={filteredEvents
                  .filter(e => e.location && e.location.lat && e.location.lng)
                  .map(e => ({
                    id: e.event_id,
                    event_id: e.event_id,
                    lat: e.location.lat,
                    lng: e.location.lng,
                    title: e.title,
                    start_date: e.start_date,
                    address: e.location.address,
                  }))}
              />
              <p className="text-xs text-[#737373] mt-3 text-center">
                {filteredEvents.filter(e => e.location && e.location.lat && e.location.lng).length} eventos con ubicación geográfica
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {filteredEvents.map(event => (
                <EventCard key={event.event_id} event={event} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const EventCard = ({ event }) => {
  return (
    <Link to={`/eventos/${event.event_id}`} data-testid={`event-card-${event.event_id}`}>
      <div className="group bg-white border border-[#E5E5E5] rounded-lg overflow-hidden hover:border-primary transition-all hover:-translate-y-1">
        <div className="relative aspect-[16/10] bg-[#F5F5F5]">
          <FavoriteButton eventId={event.event_id} variant="overlay" />
          <EventMediaGallery
            images={event.images || []}
            videoUrl={event.video_url || null}
            alt={event.title}
            variant="card"
          />
          {event.is_solidarity && (
            <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
              <Heart className="w-4 h-4" />
              Solidario
            </div>
          )}
        </div>

        <div className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-xs text-[#737373]">
            <span className="px-2 py-1 bg-primary/10 text-primary rounded">{event.category}</span>
            {event.price === 0 ? (
              <span className="px-2 py-1 bg-green-500/10 text-green-400 rounded">Gratis</span>
            ) : PAYMENTS_ENABLED ? (
              <span className="px-2 py-1 bg-[#F5F5F5] text-[#525252] rounded">{event.currency} {event.price.toLocaleString('es-AR')}</span>
            ) : null}
          </div>
          <h3 className="text-lg font-semibold text-[#0A0A0A] group-hover:text-primary transition-colors line-clamp-2">
            {event.title}
          </h3>
          {event.rating_count > 0 && (
            <div className="flex items-center gap-1 text-sm">
              <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              <span className="font-semibold text-[#0A0A0A]">{event.rating_avg?.toFixed(1)}</span>
              <span className="text-[#737373]">· {event.rating_count} reseña{event.rating_count !== 1 ? 's' : ''}</span>
            </div>
          )}
          <p className="text-sm text-[#737373] line-clamp-2">{event.description_short}</p>
          <div className="flex items-center gap-4 text-sm text-[#737373] pt-2 border-t border-[#E5E5E5]">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(event.start_date), 'd MMM', { locale: es })}</span>
            </div>
            {event.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>{event.location.city}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default Explorar;
